import { useState } from 'react'
import { X } from 'lucide-react'
import { useSettings } from '@/db/hooks'
import { labelStyle } from '@/lib/colour'
import { labelWords, parseLabelEntry } from '@/lib/words'

/**
 * Labels as chips in words, each with a remove, and a field that takes plain
 * words ("home", "high energy"; `key:value` still works for the rest). The
 * workspace and the system's labels are the caller's to leave out.
 */
export const LabelsEditor = ({
  entries,
  onAdd,
  onRemove,
  testid,
}: {
  entries: [string, string][]
  onAdd: (key: string, value: string) => void
  onRemove: (key: string) => void
  testid?: string
}) => {
  const settings = useSettings()
  const [draft, setDraft] = useState('')
  const add = () => {
    const kv = parseLabelEntry(draft)
    if (!kv) return
    onAdd(kv[0], kv[1])
    setDraft('')
  }
  return (
    <div>
      {entries.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {entries.map(([k, v]) => {
            const st = labelStyle(k, v, settings.label_styles)
            const words = labelWords(k, v)
            return (
              <span
                key={k}
                className="inline-flex items-center gap-1 rounded-full pl-2.5 pr-1 min-h-7 text-xs"
                style={st.style}
              >
                {st.icon ? `${st.icon} ` : ''}
                {words}
                <button
                  type="button"
                  onClick={() => onRemove(k)}
                  className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-black/10"
                  aria-label={`Remove ${words}`}
                >
                  <X size={12} aria-hidden />
                </button>
              </span>
            )
          })}
        </div>
      )}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            add()
          }
        }}
        onBlur={add}
        placeholder="Add a label: home, high energy…"
        className="w-full text-sm"
        autoCapitalize="off"
        autoCorrect="off"
        aria-label="Add a label"
        data-testid={testid}
      />
    </div>
  )
}
